import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { transactionService } from 'components/api/transactions';
import { Transaction } from 'types/transaction';
import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import CustomPagination from 'components/common/pagination/pagination';
import CopyableId from 'components/common/CopyableId/CopyableId';
import { commonGridStyles } from 'styles/gridStyles';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { POLLING_INTERVAL } from 'constants/config';
import commonStyles from 'styles/common.module.scss';

const Withdrawals: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const { userDetail } = useAppSelector((state) => state.users);
  const [data, setData] = useState<Transaction[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  });
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs());

  const fetchWithdrawals = useCallback(
    async (isBackground = false) => {
      if (!userId) return;
      if (!isBackground) setIsLoading(true);
      try {
        const response = await transactionService.getWithdrawTransactions({
          ...params,
          userId,
          siteId: userDetail?.site?.id,
          dateFrom: fromDate?.toISOString(),
          dateTo: toDate?.toISOString(),
        });
        setData(response.data.docs);
        setTotal(response.data.totalDocs);
      } catch (error) {
        console.error('Failed to fetch withdraw transactions:', error);
      } finally {
        if (!isBackground) setIsLoading(false);
      }
    },
    [userId, params, fromDate, toDate, userDetail?.site?.id],
  );

  const handleDateChange = (from: Dayjs | null, to: Dayjs | null) => {
    setFromDate(from);
    setToDate(to);
    setParams((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchWithdrawals();
    const interval = setInterval(() => {
      fetchWithdrawals(true);
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchWithdrawals]);

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const columns: GridColDef<Transaction>[] = [
    { field: 'id', headerName: 'ID', width: 100 },
    {
      field: 'externalTxId',
      headerName: 'External Tx Id',
      width: 300,
      renderCell: (params) => <CopyableId value={params.value} label="external transaction id" />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      renderCell: (params: any) => `$${params.value?.toLocaleString() || '0'}`,
    },
    {
      field: 'cryptoName',
      headerName: 'Method',
      width: 200,
      renderCell: (params: any) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '12px' }}>
          {params.row?.methodImageUrl && (
            <img
              style={{ marginRight: '6px' }}
              width={20}
              src={params.row.methodImageUrl}
              alt={params.row.cryptoName}
            />
          )}
          <Typography fontSize={12}>{params.row.cryptoName || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: any) => (
        <Box
          sx={{
            color:
              params.value === 'completed'
                ? '#4caf50'
                : params.value === 'rejected' ||
                    params.value === 'failed' ||
                    params.value === 'cancelled'
                  ? '#f44336'
                  : '#ff9800',
            fontWeight: 600,
            textTransform: 'capitalize',
            mt: '12px',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value: any, row: Transaction) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString() : '-',
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: 'auto',
        p: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{ textAlign: 'left', color: 'white', fontWeight: 600, fontSize: '0.8rem', mb: 1 }}
      >
        Withdraw history for user - {userDetail?.username}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <RangeDatePicker
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={(val) => handleDateChange(val, toDate)}
          onToChange={(val) => handleDateChange(fromDate, val)}
        />
      </Box>

      <Box
        className={commonStyles.gridWrapper}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {renderPagination()}
        <DataGrid
          rows={data}
          rowCount={total}
          columns={columns}
          loading={isLoading}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default Withdrawals;
